import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CONTROLSEAT_URL,
  fetchSystemInfo,
  fetchLicense,
  uploadLicense as apiUploadLicense,
  removeLicense as apiRemoveLicense,
} from "./api";

// LicenseContext exposes the on-prem license state to every component in
// the gateway. In cloud SaaS deployments the backend's GET /api/license
// returns 404, which we surface as `licensingEnabled: false` so the nav,
// banner, and License page hide themselves.
//
// It also exposes `products` — the SKU entitlement set from GET
// /api/system-info (e.g. ["scada"], ["intelligence"], or both). This is
// independent of on-prem licensing: it gates SCADA-only vs
// intelligence-only nav/routes/search regardless of licensing mode.
//
// Shape:
//   {
//     licensingEnabled: boolean,
//     loading: boolean,
//     mode: "licensed" | "expired" | "unlicensed" | null,
//     customer: string,
//     installId: string,
//     edition: string,
//     expiresAt: string | null,
//     issuedAt: string | null,
//     daysRemaining: number,
//     lastLoadedAt: string | null,
//     recentAudit: AuditEvent[],
//     products: string[],
//     refresh(): Promise<void>,
//     uploading: boolean,
//     uploadError: string | null,
//     uploadLicense(file: File): Promise<void>,
//   }
const LicenseContext = createContext(null);

const POLL_INTERVAL_MS = 60_000;
const ACTIVATION_TIMEOUT_MS = 5 * 60_000;

// Assume both SKUs until /api/system-info answers, so nav/routes don't
// flicker items away and then back while the fetch is in flight.
const DEFAULT_PRODUCTS = Object.freeze(["intelligence", "scada"]);

function normalizeProducts(raw) {
  return Array.isArray(raw) && raw.length > 0 ? raw : DEFAULT_PRODUCTS;
}

const EMPTY = {
  licensingEnabled: false,
  loading: true,
  mode: null,
  customer: "",
  installId: "",
  gatewayInstallId: "",
  gatewayFingerprint: "",
  hostFingerprint: "",
  edition: "",
  expiresAt: null,
  issuedAt: null,
  daysRemaining: 0,
  lastLoadedAt: null,
  recentAudit: [],
  products: DEFAULT_PRODUCTS,
};

function fromPayload(payload) {
  return {
    licensingEnabled: true,
    loading: false,
    mode: payload?.mode ?? null,
    customer: payload?.customer ?? "",
    installId: payload?.install_id ?? "",
    gatewayInstallId: payload?.gateway_install_id ?? "",
    gatewayFingerprint: payload?.gateway_fingerprint ?? "",
    hostFingerprint: payload?.host_fingerprint ?? "",
    edition: payload?.edition ?? "",
    expiresAt: payload?.expires_at ?? null,
    issuedAt: payload?.issued_at ?? null,
    daysRemaining:
      typeof payload?.days_remaining === "number" ? payload.days_remaining : 0,
    lastLoadedAt: payload?.last_loaded_at ?? null,
    recentAudit: Array.isArray(payload?.recent_audit) ? payload.recent_audit : [],
  };
}

export function LicenseProvider({ children }) {
  const [state, setState] = useState({ ...EMPTY, loading: true });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const systemInfo = await fetchSystemInfo().catch(() => null);
      if (!mountedRef.current) return;
      const products = normalizeProducts(systemInfo?.products);
      if (systemInfo?.licensing_enabled === false) {
        setState({ ...EMPTY, loading: false, products });
        return;
      }

      const payload = await fetchLicense();
      if (!mountedRef.current) return;
      if (payload === null) {
        // 404 — cloud SaaS, licensing disabled.
        setState({ ...EMPTY, loading: false, products });
        return;
      }
      setState({ ...fromPayload(payload), products });
    } catch {
      if (!mountedRef.current) return;
      // Treat unreachable backend the same as a transient error: stay in
      // the previous state but clear the loading flag so the UI renders
      // something instead of a perpetual spinner.
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  // Initial load + polling. Polling is cheap (one small JSON GET) and
  // keeps daysRemaining honest in long-running browser sessions; it's
  // skipped entirely once we know licensing is disabled.
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state.licensingEnabled) return undefined;
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [state.licensingEnabled, refresh]);

  const uploadLicense = useCallback(
    async (file) => {
      setUploading(true);
      setUploadError(null);
      try {
        const payload = await apiUploadLicense(file);
        if (!mountedRef.current) return;
        // /admin/license doesn't return `products` (that's a system-info
        // field) — carry forward whatever the last system-info fetch found.
        setState((prev) => ({ ...fromPayload(payload), products: prev.products }));
      } catch (err) {
        if (mountedRef.current) {
          setUploadError(err?.message || "Upload failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) setUploading(false);
      }
    },
    []
  );

  // removeLicense calls DELETE /admin/license — deletes the on-disk
  // file and swaps the running gateway to unlicensed. Used by the
  // License page's "Remove license" button. The returned payload (or
  // a fresh refetch) updates the context state in the same render
  // cycle so the UI reflects the new state immediately, including
  // auto-dismissing the license toast.
  const removeLicense = useCallback(async () => {
    setUploading(true);
    setUploadError(null);
    try {
      const payload = await apiRemoveLicense();
      if (!mountedRef.current) return;
      setState((prev) => ({ ...fromPayload(payload), products: prev.products }));
    } catch (err) {
      if (mountedRef.current) {
        setUploadError(err?.message || "Remove failed");
      }
      throw err;
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  }, []);

  // activateOnline opens a controlseat.com popup that calls
  // /api/licenses/issue using the user's website session cookies and
  // sends the resulting license JSON back via window.postMessage. The
  // gateway then uploads the file to its own /admin/license endpoint —
  // no copy-paste, no leaving the page.
  //
  // Resolves to { delivered: true } on success, { delivered: false,
  // reason } if the user closed the popup or it timed out. Rejects if
  // the upload step fails after a license arrives.
  const activateOnline = useCallback(async () => {
    if (!state.gatewayInstallId) {
      throw new Error("Gateway install_id is not loaded yet");
    }
    const expectedOrigin = new URL(CONTROLSEAT_URL).origin;
    const returnOrigin = window.location.origin;
    const popupURL = new URL("/activate", CONTROLSEAT_URL);
    popupURL.searchParams.set("install_id", state.gatewayInstallId);
    if (state.gatewayFingerprint) {
      // Hardware binding: the website passes this through to the
      // license-issuance API which signs it into the license payload.
      // Loader on the gateway rejects mismatches, so cloning the DB
      // volume to a second host produces a different fingerprint and
      // the copied license stops working.
      popupURL.searchParams.set("fingerprint", state.gatewayFingerprint);
    }
    popupURL.searchParams.set("return_origin", returnOrigin);

    const popup = window.open(
      popupURL.toString(),
      "cs-license-activate",
      "width=560,height=640"
    );
    if (!popup) {
      throw new Error(
        "Popup blocked. Allow pop-ups for this site, or use the manual flow."
      );
    }

    const licensePayload = await new Promise((resolve, reject) => {
      let settled = false;
      let closedTimer;
      let timeoutTimer;
      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(closedTimer);
        clearTimeout(timeoutTimer);
      };
      const onMessage = (event) => {
        // Strict origin check — anyone could postMessage us, only
        // controlseat.com is trusted to deliver a real license.
        if (event.source !== popup) return;
        if (event.origin !== expectedOrigin) return;
        const data = event.data;
        if (
          !data ||
          data.type !== "cs-license-activated" ||
          !data.license_file
        ) {
          return;
        }
        if (settled) return;
        settled = true;
        cleanup();
        resolve(data.license_file);
      };
      window.addEventListener("message", onMessage);
      // Fall back to "user closed it" detection. The popup's own
      // success path closes itself, so a closed popup with no message
      // means the user bailed.
      closedTimer = setInterval(() => {
        if (popup.closed && !settled) {
          settled = true;
          cleanup();
          reject(new Error("Activation cancelled"));
        }
      }, 400);
      timeoutTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        try {
          popup.close();
        } catch {}
        reject(new Error("Activation timed out"));
      }, ACTIVATION_TIMEOUT_MS);
    });

    // Wrap the JSON object as a File so we reuse the existing
    // multipart upload path, which already does the binding check
    // and atomic disk write.
    const file = new File(
      [JSON.stringify(licensePayload, null, 2)],
      "license.json",
      { type: "application/json" }
    );
    await uploadLicense(file);
  }, [state.gatewayInstallId, state.gatewayFingerprint, uploadLicense]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      uploading,
      uploadError,
      uploadLicense,
      removeLicense,
      activateOnline,
    }),
    [
      state,
      refresh,
      uploading,
      uploadError,
      uploadLicense,
      removeLicense,
      activateOnline,
    ]
  );

  return (
    <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
  );
}

export function useLicense() {
  const ctx = useContext(LicenseContext);
  if (!ctx) {
    // Outside the provider tree: pretend licensing is disabled so the
    // banner/nav hide gracefully instead of crashing on undefined access.
    return {
      ...EMPTY,
      loading: false,
      refresh: async () => {},
      uploading: false,
      uploadError: null,
      uploadLicense: async () => {},
      removeLicense: async () => {},
      activateOnline: async () => {},
    };
  }
  return ctx;
}
